import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AnalyticsData, AnalyticsType, FocusStats } from '../../components/page-components/Dashboard/types';
import api from '../../api';

// Default analytics data with 0 values
const getDefaultWeeklyData = () => ({
  days: [
    { day: 'Mon', hours: 0, sessions: 0, date: '' },
    { day: 'Tue', hours: 0, sessions: 0, date: '' },
    { day: 'Wed', hours: 0, sessions: 0, date: '' },
    { day: 'Thu', hours: 0, sessions: 0, date: '' },
    { day: 'Fri', hours: 0, sessions: 0, date: '' },
    { day: 'Sat', hours: 0, sessions: 0, date: '' },
    { day: 'Sun', hours: 0, sessions: 0, date: '' }
  ],
  average: 0,
  streak: 0
});

const getDefaultMonthlyData = () => ({
  calendar: Array.from({ length: 31 }, (_, i) => ({
    date: i + 1,
    hours: 0,
    sessions: 0,
    isToday: false
  })),
  totalHours: 0
});

const getDefaultYearlyData = () => ({
  months: [
    { month: 'Jan', hours: 0, sessions: 0, isPeak: false },
    { month: 'Feb', hours: 0, sessions: 0, isPeak: false },
    { month: 'Mar', hours: 0, sessions: 0, isPeak: false },
    { month: 'Apr', hours: 0, sessions: 0, isPeak: false },
    { month: 'May', hours: 0, sessions: 0, isPeak: false },
    { month: 'Jun', hours: 0, sessions: 0, isPeak: false },
    { month: 'Jul', hours: 0, sessions: 0, isPeak: false },
    { month: 'Aug', hours: 0, sessions: 0, isPeak: false },
    { month: 'Sep', hours: 0, sessions: 0, isPeak: false },
    { month: 'Oct', hours: 0, sessions: 0, isPeak: false },
    { month: 'Nov', hours: 0, sessions: 0, isPeak: false },
    { month: 'Dec', hours: 0, sessions: 0, isPeak: false }
  ],
  totalHours: 0
});

// Work Log Data Interface
export interface WorkLogData {
  period: 'week' | 'month' | 'year';
  totalHours: number;
  sessions: number;
  averageSession: number;
  productivity: number;
  data: Array<{
    day?: string;
    week?: string;
    month?: string;
    hours: number;
    sessions: number;
    productivity: number;
  }>;
}

// Custom range aggregated data
export interface CustomRangeData {
  totalHours: number;
  totalSessions: number;
  averageSession: number;
  data: Array<{ day?: string; week?: string; month?: string; hours: number; sessions: number }>;
}

// Analytics state interface
interface AnalyticsState {
  data: AnalyticsData;
  stats: FocusStats | null;
  workLogData: WorkLogData | null;
  customRangeData: CustomRangeData | null;
  currentTab: AnalyticsType;
  isLoading: boolean;
  isLoadingStats: boolean;
  isLoadingWorkLog: boolean;
  error: string | null;
  statsError: string | null;
  workLogError: string | null;
}

// Initial state with default values
const initialState: AnalyticsState = {
  data: {
    weekly: getDefaultWeeklyData(),
    monthly: getDefaultMonthlyData(),
    yearly: getDefaultYearlyData(),
  },
  stats: null,
  workLogData: null,
  customRangeData: null,
  currentTab: AnalyticsType.WEEKLY,
  isLoading: false,
  isLoadingStats: false,
  isLoadingWorkLog: false,
  error: null,
  statsError: null,
  workLogError: null,
};

// Async thunk to fetch analytics data
export const fetchAnalytics = createAsyncThunk(
  'analytics/fetch',
  async ({ type, projectId }: { type: AnalyticsType; projectId?: string }, { rejectWithValue }) => {
    try {
      const data = await api.focus.getAnalytics(type, projectId);
      return { type, data };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch analytics data'
      );
    }
  }
);

// Async thunk to fetch focus session stats
export const fetchFocusStats = createAsyncThunk(
  'analytics/fetchStats',
  async ({ type, projectId }: { type: AnalyticsType; projectId?: string }, { rejectWithValue }) => {
    try {
      const stats = await api.focus.getStats(type, projectId);
      return stats;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch focus stats'
      );
    }
  }
);

// Async thunk to fetch and aggregate sessions for custom date range
export const fetchCustomRangeAnalytics = createAsyncThunk(
  'analytics/fetchCustomRange',
  async (
    { dateFrom, dateTo, projectId }: { dateFrom: string; dateTo: string; projectId?: string },
    { rejectWithValue }
  ) => {
    try {
      const sessions = await api.focus.getAll();
      const from = new Date(dateFrom);
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);

      const filtered = sessions.filter((s) => {
        const dateStr = s.completedAt || s.createdAt || s.updatedAt;
        if (!dateStr) return false;
        const d = new Date(dateStr);
        if (d < from || d > to) return false;
        if (projectId && s.projectId !== projectId) return false;
        return true;
      });

      const rangeDays = Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
      const totalHours = filtered.reduce((sum, s) => sum + (s.duration || 0) / 60, 0);
      const totalSessions = filtered.length;
      const averageSession = totalSessions > 0 ? totalHours / totalSessions : 0;

      type GroupKey = string;
      const groupMap = new Map<GroupKey, { hours: number; sessions: number; label: string }>();

      const toKey = (d: Date) => d.toISOString().slice(0, 10);
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

      filtered.forEach((s) => {
        const dateStr = s.completedAt || s.createdAt || s.updatedAt;
        const d = new Date(dateStr!);
        const hours = (s.duration || 0) / 60;

        let key: GroupKey;
        let label: string;

        if (rangeDays <= 31) {
          key = toKey(d);
          label = `${monthNames[d.getMonth()]} ${d.getDate()}`;
        } else if (rangeDays <= 90) {
          const weekStart = new Date(d);
          weekStart.setDate(d.getDate() - d.getDay());
          key = `w-${toKey(weekStart)}`;
          label = `Week of ${monthNames[weekStart.getMonth()]} ${weekStart.getDate()}`;
        } else {
          key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
          label = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
        }

        const existing = groupMap.get(key);
        if (existing) {
          existing.hours += hours;
          existing.sessions += 1;
        } else {
          groupMap.set(key, { hours, sessions: 1, label });
        }
      });

      const data = Array.from(groupMap.entries())
        .map(([sortKey, val]) => ({
          ...(rangeDays <= 31 ? { day: val.label } : rangeDays <= 90 ? { week: val.label } : { month: val.label }),
          hours: val.hours,
          sessions: val.sessions,
          _sortKey: sortKey,
        }))
        .sort((a, b) => ((a as any)._sortKey || '').localeCompare((b as any)._sortKey || ''))
        .map(({ _sortKey, ...rest }) => rest);

      return {
        totalHours,
        totalSessions,
        averageSession,
        data,
      } as CustomRangeData;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch custom range data'
      );
    }
  }
);

// Async thunk to fetch work log analytics
export const fetchWorkLogAnalytics = createAsyncThunk(
  'analytics/fetchWorkLog',
  async ({ period, projectId }: { period: 'week' | 'month' | 'year'; projectId?: string }, { rejectWithValue }) => {
    try {
      const data = await api.focus.getWorkLogAnalytics(period, projectId);
      return data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch work log analytics'
      );
    }
  }
);

// Async thunk to download work log
export const downloadWorkLog = createAsyncThunk(
  'analytics/downloadWorkLog',
  async ({ period, format, projectId }: { period: 'week' | 'month' | 'year'; format: 'pdf' | 'csv' | 'excel'; projectId?: string }, { rejectWithValue }) => {
    try {
      const blob = await api.focus.downloadWorkLog(period, format, projectId);
      return { blob, period, format };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to download work log'
      );
    }
  }
);

// Analytics slice
const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    setCurrentTab: (state, action: PayloadAction<AnalyticsType>) => {
      state.currentTab = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetAnalytics: (state) => {
      state.data = {
        weekly: getDefaultWeeklyData(),
        monthly: getDefaultMonthlyData(),
        yearly: getDefaultYearlyData(),
      };
      state.error = null;
    },
    setSelectedProject: (state, action: PayloadAction<string | undefined>) => {
      // This can be used to track selected project in analytics state if needed
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAnalytics.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAnalytics.fulfilled, (state, action) => {
        state.isLoading = false;
        const { type, data } = action.payload;

        // Update the specific analytics type in the state
        if (type === AnalyticsType.WEEKLY) {
          state.data.weekly = data.weekly || getDefaultWeeklyData();
        } else if (type === AnalyticsType.MONTHLY) {
          state.data.monthly = data.monthly || getDefaultMonthlyData();
        } else if (type === AnalyticsType.YEARLY) {
          state.data.yearly = data.yearly || getDefaultYearlyData();
        }
      })
      .addCase(fetchAnalytics.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchFocusStats.pending, (state) => {
        state.isLoadingStats = true;
        state.statsError = null;
      })
      .addCase(fetchFocusStats.fulfilled, (state, action) => {
        state.isLoadingStats = false;
        state.stats = action.payload;
      })
      .addCase(fetchFocusStats.rejected, (state, action) => {
        state.isLoadingStats = false;
        state.statsError = action.payload as string;
      })
      .addCase(fetchCustomRangeAnalytics.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomRangeAnalytics.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customRangeData = action.payload;
      })
      .addCase(fetchCustomRangeAnalytics.rejected, (state, action) => {
        state.isLoading = false;
        state.customRangeData = null;
        state.error = action.payload as string;
      })
      .addCase(fetchWorkLogAnalytics.pending, (state) => {
        state.isLoadingWorkLog = true;
        state.workLogError = null;
      })
      .addCase(fetchWorkLogAnalytics.fulfilled, (state, action) => {
        state.isLoadingWorkLog = false;
        state.workLogData = action.payload;
      })
      .addCase(fetchWorkLogAnalytics.rejected, (state, action) => {
        state.isLoadingWorkLog = false;
        state.workLogError = action.payload as string;
      })
      .addCase(downloadWorkLog.pending, (state) => {
        // Can add loading state for download if needed
      })
      .addCase(downloadWorkLog.fulfilled, (state, action) => {
        // Handle download - create blob URL and trigger download
        const { blob, period, format } = action.payload;

        // Validate that we have a valid blob
        if (!blob || !(blob instanceof Blob)) {
          state.workLogError = 'Invalid file data received from server';
          return;
        }

        // Determine MIME type and file extension based on format
        let mimeType: string;
        let fileExtension: string;

        switch (format) {
          case 'pdf':
            mimeType = 'application/pdf';
            fileExtension = 'pdf';
            break;
          case 'csv':
            mimeType = 'text/csv';
            fileExtension = 'csv';
            break;
          case 'excel':
            mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            fileExtension = 'xlsx';
            break;
          default:
            mimeType = 'application/octet-stream';
            fileExtension = format;
        }

        // Create a new blob with the correct MIME type
        const typedBlob = new Blob([blob], { type: mimeType });
        const url = URL.createObjectURL(typedBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `worklog-${period}-${new Date().toISOString().split('T')[0]}.${fileExtension}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        // Revoke the URL after a short delay to ensure download starts
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 100);
      })
      .addCase(downloadWorkLog.rejected, (state, action) => {
        state.workLogError = action.payload as string;
      });
  },
});

export const { setCurrentTab, setError, clearError, resetAnalytics } = analyticsSlice.actions;
export default analyticsSlice.reducer;

// Export WorkLogData type
// WorkLogData is already exported at definition